import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiPhone, HiEnvelope, HiMapPin, HiClock, HiGlobeAlt, HiBuildingOffice } from 'react-icons/hi2';
import SectionTitle from '../components/SectionTitle';
import ContactCard from '../components/ContactCard';
import InfoBox from '../components/InfoBox';

const Contact = () => {
  const { t } = useTranslation();

  const organizations = [
    {
      name: 'FPRA - Fondation pour la Promotion des Droits',
      description: t('contact.fpraDescription'),
      phone: '+213 560 667 120',
      email: 'fpra2024@gmail.com',
      address: '25 rue Rabah Bourbia, El Biar, Alger',
      hours: t('contact.fpraHours'),
      website: 'https://fpra-droits.org/',
      color: 'teal'
    },
    {
      name: 'UNHCR Algeria (HCR)',
      description: t('contact.unhcrDescription'),
      phone: '+213 23 05 28 53',
      phoneEmergency: '+213 661 69 68 41',
      email: 'algdz@unhcr.org',
      emailRegistration: 'algdzreg@unhcr.org',
      address: '128 chemin Bachir El-Ibrahimi, Poirson, El-Biar, 16000 Alger',
      hours: t('contact.unhcrHours'),
      website: 'https://help.unhcr.org/algeria/',
      color: 'blue'
    },
    {
      name: 'IOM Algeria (OIM)',
      description: t('contact.iomDescription'),
      phone: '+213 661 464 622',
      email: 'iomalgiers@iom.int',
      address: '16 Chemin des Crêtes Hydra, Alger',
      website: 'https://algeria.iom.int/',
      color: 'orange'
    },
    {
      name: t('contact.redCrescentName'),
      description: t('contact.redCrescentDescription'),
      phone: '028 16 62 24',
      phoneEmergency: '+213 560 001 338',
      address: '22 boulevard Colonel Bougara, El Biar, Alger',
      color: 'red'
    },
    {
      name: t('contact.caritasName'),
      description: t('contact.caritasDescription'),
      phone: '+213 21 92 58 55',
      email: 'caritas@caritas.dz',
      address: '22 Chemin d\'Hydra, 16030 El Biar, Alger',
      website: 'http://www.caritas.dz',
      color: 'purple'
    },
    {
      name: t('contact.foreignersBureauName'),
      description: t('contact.foreignersBureauDescription'),
      phone: '021 73 00 73',
      address: '20, bd Zighout Youcef, Wilaya d\'Alger',
      color: 'gray'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      teal: {
        border: 'border-teal-500',
        icon: 'text-teal-600 dark:text-teal-400'
      },
      blue: {
        border: 'border-blue-500',
        icon: 'text-blue-600 dark:text-blue-400'
      },
      orange: {
        border: 'border-orange-500',
        icon: 'text-orange-600 dark:text-orange-400'
      },
      red: {
        border: 'border-red-500',
        icon: 'text-red-600 dark:text-red-400'
      },
      purple: {
        border: 'border-purple-500',
        icon: 'text-purple-600 dark:text-purple-400'
      },
      gray: {
        border: 'border-gray-500',
        icon: 'text-gray-600 dark:text-gray-400'
      }
    };
    return colorMap[color] || colorMap.gray;
  };

  const OrganizationCard = ({ org }) => {
    const colors = getColorClasses(org.color);
    
    return (
      <div className={`bg-white dark:bg-gray-800 border-s-4 ${colors.border} rounded-e-lg shadow-sm p-6 space-y-4`}>
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{org.name}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{org.description}</p>
        </div>

        <div className="space-y-2">
          {org.phone && (
            <div className="flex items-start gap-3">
              <HiPhone className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{org.phone}</p>
                {org.phoneEmergency && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('contact.emergency')}: {org.phoneEmergency}
                  </p>
                )}
              </div>
            </div>
          )}

          {org.email && (
            <div className="flex items-start gap-3">
              <HiEnvelope className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
              <div>
                <a href={`mailto:${org.email}`} className="text-sm text-teal-600 dark:text-teal-400 hover:underline">
                  {org.email}
                </a>
                {org.emailRegistration && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('contact.registration')}: {org.emailRegistration}
                  </p>
                )}
              </div>
            </div>
          )}

          {org.address && (
            <div className="flex items-start gap-3">
              <HiMapPin className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
              <p className="text-sm text-gray-700 dark:text-gray-300">{org.address}</p>
            </div>
          )}

          {org.hours && (
            <div className="flex items-start gap-3">
              <HiClock className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
              <p className="text-sm text-gray-700 dark:text-gray-300">{org.hours}</p>
            </div>
          )}

          {org.website && (
            <div className="flex items-start gap-3">
              <HiGlobeAlt className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
              <a 
                href={org.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-teal-600 dark:text-teal-400 hover:underline"
              >
                {org.website}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <SectionTitle level={1} icon={HiPhone}>
        {t('contact.title')}
      </SectionTitle>

      {/* Emergency info */}
      <InfoBox type="important" title={t('contact.emergencyTitle')}>
        <p>{t('contact.emergencyContent')}</p>
      </InfoBox>

      {/* Organizations */}
      <section>
        <SectionTitle level={2} icon={HiBuildingOffice}>{t('contact.organizationsTitle')}</SectionTitle>
        <div className="space-y-4">
          {organizations.map((org, index) => (
            <OrganizationCard key={index} org={org} />
          ))}
        </div>
      </section>

      {/* Additional help */}
      <InfoBox type="conseil" title={t('home.needHelpTitle')}>
        <p>{t('home.needHelpContent')}</p>
      </InfoBox>
    </div>
  );
};

export default Contact;
